import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface MemberNavProps {
  currentPage: 'dashboard' | 'profile' | 'competitions';
  onNavigate: (page: 'dashboard' | 'profile' | 'competitions' | 'home') => void;
}

const MemberNav: React.FC<MemberNavProps> = ({ currentPage, onNavigate }) => {
  const { user, signOut, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    onNavigate('home');
  };

  const navItems = [
    { id: 'dashboard' as const, label: 'Home', icon: '🏠' },
    { id: 'competitions' as const, label: 'Competitions', icon: '🏆' },
    { id: 'profile' as const, label: 'Profile', icon: '👤' },
  ];

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <button
                onClick={() => onNavigate('home')}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
              >
                <img src="/tumbulgum-logo.png" alt="Tumbulgum Fishing Club" className="h-10 w-auto" />
              </button>
            </div>
            <div className="hidden md:ml-8 md:flex md:space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    currentPage === item.id
                      ? 'bg-fishing-blue text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="mr-2">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-3">
              <button onClick={() => onNavigate('home')} className="text-sm text-fishing-blue hover:underline">← Website</button>
              {isAdmin && (
                <button
                  onClick={() => window.location.assign('/admin')}
                  className="text-sm text-gray-600 hover:text-gray-800 hover:underline"
                >
                  Admin
                </button>
              )}
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.email || 'Member'}</p>
                <p className="text-xs text-gray-500">Signed in</p>
              </div>
              <button onClick={handleSignOut} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue transition-colors">
                Logout
              </button>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-fishing-blue"
            >
              <span className="sr-only">Open menu</span>
              {mobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { onNavigate(item.id); setMobileMenuOpen(false); }}
                className={`w-full text-left flex items-center px-3 py-2 text-base font-medium rounded-md transition-colors ${
                  currentPage === item.id ? 'bg-fishing-blue text-white' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
          <div className="pt-4 pb-3 border-t border-gray-200">
            <div className="px-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.email || 'Member'}</p>
                <p className="text-xs text-gray-500">Signed in</p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => window.location.assign('/admin')}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue transition-colors"
                >
                  Switch to Admin
                </button>
              )}
              <button onClick={handleSignOut} className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue transition-colors">
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default MemberNav;
